/**
 * @file itemSupplier.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-02
 * @modified 2026-11-02
 * @description An inventory item's SUPPLIER (Cam, 30 Oct / 2 Nov 2026) — who
 *              it is bought from, kept on the item (inventory.items
 *              supplier_account_id / supplier_group_parent_id,
 *              migrations/item_supplier.sql; local only, never sent to Xero),
 *              and what the supplier pickers do with it.
 *
 *   Written once here; utils/itemSupplier.js on the server is GENERATED from
 *   it by scripts/sync-shared-utils.js.
 *
 *   THE GROUP RULE (Cam, firm — "always make users pick a supplier for any
 *   MVFS supplier group member rather than auto-fill one"): an item bought
 *   from ANY member of a supplier group — the parent (Mole Valley Feed
 *   Solutions) or a depot (MVFS - Lifton…) — is stored as the GROUP, never
 *   the member (storedSupplier). So an item names one supplier contact (in no
 *   group), one supplier group, or nothing. `groups` everywhere below is the
 *   map utils/supplierGroups.groupMapFromRows makes: account id → its group's
 *   parent id (a parent maps to itself; an account in no group is absent).
 *
 *   THE PICKERS:
 *   - A line's own supplier (Create / Edit Customer Order, Create Invoice):
 *     picking an item whose supplier is ONE contact fills the line's supplier
 *     only when the line has none (never over one chosen or loaded), and only
 *     when that supplier is in the list offered (lineSupplierFill). A group
 *     fills nothing. Either way the item's supplier — or its group's members —
 *     is pinned at the top of the line's supplier list (pinnedSupplierIds →
 *     pinSupplierOptions, SearchableDropdown's `sticky`: first, in view while
 *     the rest scrolls).
 *   - A header supplier (Create / Edit PO, Add / Edit Bill): every line's
 *     item's supplier is pinned; the header is filled only when it has none
 *     and every line with an item names the same single supplier
 *     (headerSupplierFill).
 *   - No supplier on the item: the pickers do what they did before.
 */

/** Before migrations/item_supplier.sql has run (the screens and the 409). */
export const NOT_READY_REASON = 'Not ready — migration item_supplier.sql has not run';

/** The pinned options' tags (SearchableDropdown draws them as pills). */
export const PIN_TAG_SUPPLIER = "Item's supplier";
export const PIN_TAG_GROUP = "Item's group";

const idOf = (v) => {
  const n = v === null || v === undefined || v === '' ? NaN : Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/* ------------------------------------------------------------ choices */

/**
 * The key a choice travels as (the dropdowns' values and the PUT's body):
 * 'a:<account id>' a supplier, 'g:<group parent id>' a supplier group,
 * '' none.
 */
export function supplierKey(choice) {
  if (!choice) return '';
  const id = idOf(choice.id ?? choice.accountId ?? choice.parentId);
  if (!id) return '';
  return choice.kind === 'group' ? `g:${id}` : `a:${id}`;
}

/**
 * A key read back: { kind: 'supplier'|'group', id } — null for none (''
 * or null), undefined for something that is neither.
 */
export function parseSupplierKey(key) {
  if (key === null || key === undefined || String(key).trim() === '') return null;
  const m = /^\s*([ag]):(\d+)\s*$/i.exec(String(key));
  if (!m) return undefined;
  const id = idOf(m[2]);
  if (!id) return undefined;
  return { kind: m[1].toLowerCase() === 'g' ? 'group' : 'supplier', id };
}

/** account id → group parent id, from [{ parent_id, member_ids }] (the options' groups). */
export function groupMapFrom(groups = []) {
  const map = new Map();
  for (const g of groups || []) {
    const parent = idOf(g?.parent_id ?? g?.parentId);
    if (!parent) continue;
    map.set(parent, parent);
    for (const m of g.member_ids || g.memberIds || []) {
      const id = idOf(m);
      if (id) map.set(id, parent);
    }
  }
  return map;
}

/**
 * What is stored for a choice — the group rule.
 * @param {{ kind, id }|null} choice
 * @param {Map<number, number>} groups account id → group parent id
 * @returns {{ supplierAccountId: number|null, groupParentId: number|null, fromMember: number|null }
 *          | { error: string }}
 *          fromMember: the member chosen when it was stored as its group
 */
export function storedSupplier(choice, groups = new Map()) {
  if (!choice) return { supplierAccountId: null, groupParentId: null, fromMember: null };
  const id = idOf(choice.id);
  if (!id) return { error: 'Choose a supplier' };
  if (choice.kind === 'group') {
    if (groups.get(id) !== id) return { error: 'That is not a supplier group' };
    return { supplierAccountId: null, groupParentId: id, fromMember: null };
  }
  if (choice.kind !== 'supplier') return { error: 'Choose a supplier' };
  const parent = groups.get(id);
  if (parent) return { supplierAccountId: null, groupParentId: parent, fromMember: id };
  return { supplierAccountId: id, groupParentId: null, fromMember: null };
}

/** The choice an item's stored columns are. */
export function choiceOfItem(row) {
  const group = idOf(row?.supplier_group_parent_id);
  if (group) return { kind: 'group', id: group };
  const account = idOf(row?.supplier_account_id);
  return account ? { kind: 'supplier', id: account } : null;
}

/* ------------------------------------------------------------- words */

/** A group as the screens name it: "Mole Valley Feed Solutions group". */
export const groupName = (parentName) => `${String(parentName || '').trim() || 'Supplier'} group`;

/** A group in the item's Supplier dropdown. */
export const groupOptionLabel = (parentName) => `${groupName(parentName)} (any depot — pick on the order)`;

/**
 * The item's supplier in words for a cell, a window or the export:
 * "NWF Agriculture", "Mole Valley Feed Solutions group", '' for none.
 * Reads { supplier_name, supplier_group_parent_id } (the search's rows) or
 * { kind, name } (the item window's).
 */
export function supplierText(row) {
  if (!row) return '';
  const name = String(row.supplier_name ?? row.name ?? '').trim();
  if (!name) return '';
  const isGroup = row.kind === 'group' || !!idOf(row.supplier_group_parent_id);
  return isGroup ? groupName(name) : name;
}

/** Said when a member was chosen and stored as its group. */
export const storedAsGroupText = (memberName, parentName) =>
  `${memberName || 'That supplier'} is in the ${groupName(parentName)}, so the item's supplier is the group — the depot is picked on each order.`;

/* ----------------------------------------------------------- pickers */

/**
 * The supplier an item row names, for the pickers — from the `item_supplier`
 * the server attaches to item rows ({ kind, account_id } or { kind:
 * 'group', parent_id, member_ids }); null when it names none.
 * @returns {{ kind: 'supplier', accountId } | { kind: 'group', parentId, memberIds: number[] } | null}
 */
export function itemSupplierOf(item) {
  const s = item?.item_supplier;
  if (!s) return null;
  if (s.kind === 'supplier') {
    const accountId = idOf(s.account_id ?? s.accountId);
    return accountId ? { kind: 'supplier', accountId } : null;
  }
  if (s.kind === 'group') {
    const parentId = idOf(s.parent_id ?? s.parentId);
    if (!parentId) return null;
    const members = (s.member_ids || s.memberIds || []).map(idOf).filter(Boolean);
    // the parent first (it bills the depots), then the depots as given
    const memberIds = [parentId, ...members.filter((m) => m !== parentId)];
    return { kind: 'group', parentId, memberIds: [...new Set(memberIds)] };
  }
  return null;
}

const idSet = (offered) => (offered === null || offered === undefined ? null
  : new Set([...offered].map((o) => idOf(typeof o === 'object' && o !== null ? (o.id ?? o.value) : o)).filter(Boolean)));

/**
 * The supplier to put on a line whose item was just picked, or null: the
 * item's ONE supplier, only when the line has none, and only when it is
 * among the suppliers offered (an archived contact is not). A group fills
 * nothing — the person picks the depot.
 * @param {object} item — the picked item row
 * @param {{ current?: *, offered?: Iterable }} opts current: the line's supplier now;
 *        offered: the supplier ids (or rows) the list holds
 */
export function lineSupplierFill(item, { current = null, offered = null } = {}) {
  if (idOf(current)) return null;
  const s = itemSupplierOf(item);
  if (!s || s.kind !== 'supplier') return null;
  const ids = idSet(offered);
  if (ids && !ids.has(s.accountId)) return null;
  return s.accountId;
}

/**
 * The suppliers to pin for these items, in order: each single supplier, then
 * each group's members (its parent first). Each once.
 * @returns {Array<{ id: number, why: 'supplier'|'group' }>}
 */
export function pinnedSupplierIds(items = []) {
  const singles = [];
  const members = [];
  const seen = new Set();
  const hints = (Array.isArray(items) ? items : [items]).map(itemSupplierOf).filter(Boolean);
  for (const h of hints) {
    if (h.kind === 'supplier' && !seen.has(h.accountId)) { seen.add(h.accountId); singles.push({ id: h.accountId, why: 'supplier' }); }
  }
  for (const h of hints) {
    if (h.kind !== 'group') continue;
    for (const id of h.memberIds) {
      if (!seen.has(id)) { seen.add(id); members.push({ id, why: 'group' }); }
    }
  }
  return [...singles, ...members];
}

const isBlankOption = (o) => o.value === '' || o.value === null || o.value === undefined;

/**
 * A supplier dropdown's options with the pinned ones first and `sticky` (in
 * view while the rest scrolls), tagged "Item's supplier" / "Item's group" in
 * the list (`listTag` — never in the trigger once chosen).
 * The blank "Select…" row stays at the very top; every other option keeps
 * its order. Option values may be numbers or strings ('12').
 */
export function pinSupplierOptions(options = [], pins = []) {
  if (!pins || !pins.length) return options;
  // the first pin of an id wins (a single supplier before a group's depots)
  const order = new Map();
  pins.forEach((p, i) => { if (p && !order.has(p.id)) order.set(p.id, { ...p, i }); });
  const pinned = [];
  const rest = [];
  for (const o of options) {
    const p = isBlankOption(o) ? null : order.get(idOf(o.value));
    if (p) pinned.push({ o, p });
    else rest.push(o);
  }
  if (!pinned.length) return options;
  pinned.sort((a, b) => a.p.i - b.p.i);
  return [
    ...rest.filter(isBlankOption),
    ...pinned.map(({ o, p }) => ({
      ...o,
      sticky: true,
      pinned: false,
      // in the list only (SearchableDropdown 1.13.0) — the trigger shows the name alone
      listTag: p.why === 'group' ? PIN_TAG_GROUP : PIN_TAG_SUPPLIER
    })),
    ...rest.filter((o) => !isBlankOption(o))
  ];
}

/**
 * A HEADER supplier (a PO's, a bill's) from its lines' items: the one
 * supplier every line with an item names — only when the header has none,
 * every such line's item names a single supplier (not a group, not none),
 * they are all the same, and it is offered. Otherwise null.
 * @param {Array<object|null>} items — the lines' items (null for a line with none)
 */
export function headerSupplierFill(items = [], { current = null, offered = null } = {}) {
  if (idOf(current)) return null;
  const withItem = (items || []).filter(Boolean);
  if (!withItem.length) return null;
  const hints = withItem.map(itemSupplierOf);
  if (hints.some((h) => !h || h.kind !== 'supplier')) return null;
  const ids = [...new Set(hints.map((h) => h.accountId))];
  if (ids.length !== 1) return null;
  const offeredIds = idSet(offered);
  if (offeredIds && !offeredIds.has(ids[0])) return null;
  return ids[0];
}

/**
 * The old item-code guess for a bill's supplier (utils/lineItems
 * suggestedSupplierFor) under the group rule: never a supplier group's
 * member (the person picks), never when the item names its own supplier.
 */
export function guessAllowed(item, guessedId, groups = new Map()) {
  if (itemSupplierOf(item)) return false;
  const id = idOf(guessedId);
  return !!id && !groups.has(id);
}

/** Every member of the group `id` is in — its parent first; [] when in none. */
export function groupMemberIds(id, groups = new Map()) {
  const x = idOf(id);
  const parent = x ? groups.get(x) : null;
  if (!parent) return [];
  const others = [...groups.entries()].filter(([m, p]) => p === parent && m !== parent).map(([m]) => m)
    .sort((a, b) => a - b);
  return [parent, ...others];
}

/** Pins for a supplier group's members (a guessed member: the person picks one). */
export const groupPins = (id, groups = new Map()) => groupMemberIds(id, groups).map((m) => ({ id: m, why: 'group' }));

/* ------------------------------------------ Search Inventory's filter */

/** "No supplier" in the filter row (`suppliers=…,none`) and the column filter's blank. */
export const NO_SUPPLIER_VALUE = 'none';
export const NO_SUPPLIER_LABEL = 'No supplier';
const COLUMN_BLANK = '(blank)';

/** Why the filter row's Supplier cannot be used yet, or null. */
export function supplierFilterBlock(lists) {
  if (!lists || !lists.loaded) return 'Reading the suppliers…';
  if (lists.ready === false) return lists.reason || NOT_READY_REASON;
  return null;
}

/**
 * The filter row's options: "No supplier", each supplier group, then each
 * supplier contact an item names (with how many), by name.
 */
export function supplierFilterOptions(lists) {
  if (supplierFilterBlock(lists)) return [];
  const count = (n) => (n ? ` (${n})` : '');
  return [
    { value: NO_SUPPLIER_VALUE, label: `${NO_SUPPLIER_LABEL}${count(lists.noneCount)}`, searchText: `${NO_SUPPLIER_LABEL} none` },
    ...(lists.groups || []).filter((g) => g.item_count > 0).map((g) => ({
      value: `g:${g.parent_id}`,
      label: `${groupName(g.name)}${count(g.item_count)}`,
      searchText: `${g.name} group ${(g.members || []).map((m) => m.name).join(' ')}`
    })),
    ...(lists.suppliers || []).filter((s) => s.item_count > 0).map((s) => ({
      value: `a:${s.id}`,
      label: `${s.name}${count(s.item_count)}`,
      searchText: `${s.account_no || ''} ${s.name || ''}`
    }))
  ];
}

/** What the closed filter says: every supplier, the one chosen, or how many. */
export function summarizeSupplierFilter(lists) {
  return (value) => {
    if (supplierFilterBlock(lists)) return lists && lists.loaded ? 'Not ready' : 'Loading…';
    const sel = (value || []).map(String);
    if (!sel.length) return 'All Suppliers';
    if (sel.length === 1) {
      if (sel[0] === NO_SUPPLIER_VALUE) return NO_SUPPLIER_LABEL;
      const o = supplierFilterOptions(lists).find((x) => x.value === sel[0]);
      return o ? o.label.replace(/ \(\d+\)$/, '') : '1 supplier';
    }
    return `${sel.length} suppliers`;
  };
}

/** The Supplier column filter's words for a value: its blank is "No supplier". */
export function supplierColumnLabel(value) {
  return value === COLUMN_BLANK ? NO_SUPPLIER_LABEL : value;
}
